import React, { useCallback, useRef, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import AdminInformationEditForm from '@/components/organisms/AdminInformationEditForm';
import { useAdminInformationDetail } from '@/hooks/admin/useAdminInformationDetail';
import { useAdminInformationUpdate } from '@/hooks/admin/useAdminInformationUpdate';
import { ADMIN_ROUTES } from '@/constants/routes';
import {
  InformationEditFormData,
  resolveServiceType,
  mergeDateTime,
} from '@/schemas/informationSchema';
import { AppBreadcrumb, AppCard, AppContainer } from '@/components/atoms';
import { isNotFoundError } from '@/utils/apiError';
import { AdminInformationPreviewModal } from '@/components/organisms';
import type { InformationPreviewData } from '@/components/organisms';

const SiteAdminInformationEditPage: React.FC = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const navigate = useNavigate();
  const informationId = Number(id);

  const { data: detailResponse, isLoading, isError, error } = useAdminInformationDetail(
    site ?? '',
    informationId
  );
  const { updateInformationMutation, isLoading: isSubmitting } = useAdminInformationUpdate();
  const [previewData, setPreviewData] = useState<InformationPreviewData | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const informationData = detailResponse?.data;
  const loadedDataRef = useRef(informationData);
  if (informationData) loadedDataRef.current = informationData;
  const stableData = informationData ?? loadedDataRef.current;

  const handleSubmit = useCallback(
    (data: InformationEditFormData) => {
      if (!site || !informationId) return;

      updateInformationMutation(
        {
          siteType: site,
          id: informationId,
          data: {
            title: data.title,
            information_date: data.information_date,
            contents: data.contents ?? null,
            display_from: mergeDateTime(data.display_from_date, data.display_from_time),
            display_to: mergeDateTime(data.display_to_date, data.display_to_time),
            is_display: data.is_display,
            service_type: resolveServiceType(data.service_type_a2, data.service_type_r2),
            tenant_ids: data.tenant_ids,
            clinic_ids: data.clinic_ids ?? [],
          },
        },
      ).then(() => {
        navigate(ADMIN_ROUTES.INFORMATIONS.LIST(site));
      }).catch(() => {
        // error toast is handled by the hook; stay on page
      });
    },
    [site, informationId, updateInformationMutation]
  );

  const handlePreview = useCallback(
    (data: InformationPreviewData) => {
      setPreviewData(data);
      setIsPreviewOpen(true);
    },
    []
  );

  if (!site) return null;

  const breadcrumbItems = [
    { title: 'お知らせマスタ', href: ADMIN_ROUTES.INFORMATIONS.LIST(site) },
    { title: `編集:${informationId}` },
  ];

  if (isLoading) {
    return (
      <AppContainer className="py-6 flex justify-center">
        <Spin size="large" />
      </AppContainer>
    );
  }

  if (isError && isNotFoundError(error) && !stableData) {
    return <Navigate to={`/${site}/admin/404`} replace />;
  }

  if (!stableData) {
    return (
      <AppContainer className="py-6">
        <p>お知らせが見つかりません。</p>
      </AppContainer>
    );
  }

  return (
    <>
      <AppContainer className="py-6 space-y-6">
        <AppBreadcrumb items={breadcrumbItems} />
        <AppCard title={`お知らせマスタ編集:${informationId}`}>
          <AdminInformationEditForm
            siteType={site}
            initialData={stableData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onPreview={handlePreview}
          />
        </AppCard>
      </AppContainer>
      <AdminInformationPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        data={previewData}
      />
    </>
  );
};

export default SiteAdminInformationEditPage;
